import { _splitPath } from './../objects/_split_path';
import { isEmpty } from '../util/is_empty';
import { is } from './../util/is';

/**
 * Takes an object and a path and returns the property of the object at the path or `undefined`.
 *
 * ```ts
 * tt.get('prop1.prop2.prop3', {
 *   prop1: {
 *     prop2: {
 *       prop3: '3'
 *     }
 *   }
 * });
 * // => '3'
 * ```
 * @param path A string representing a path in an object, e.g. `prop1[0].prop2`.
 * @param object The object containing the value to get.
 */
export function get<T>(path: string, object: Record<string, any>): T | undefined {
  return getWithDefault(undefined, path, object);
}

/**
 * Takes an object, a path and a default and returns the property of the object at the path or the default.
 *
 * ```ts
 * tt.getWithDefault(undefined, 'prop1.prop2.prop3', {
 *   prop1: {
 *     prop2: {
 *       prop3: '3'
 *     }
 *   }
 * });
 * // => '3'
 * ```
 *
 * @param defaultValue The value to be returned if value at path is `undefined`.
 * @param path A string representing a path in an object, e.g. `prop1[0].prop2`.
 * @param object The object containing the value to get.
 */
export function getWithDefault<T>(defaultValue: T, path: string, object: Record<string, any>): T {
  if (!is(String, path)) {
    return defaultValue;
  }

  return isEmpty(path) ? (object as T) : _get(object, _splitPath(path), defaultValue);
}

function _get<T>(object: Record<string, unknown> | unknown, pathSplit: string[], defaultValue: T): T {
  const next = pathSplit.pop() as string;
  const _object = (object as Record<string, unknown>)[next];

  if (is(undefined, _object)) {
    return defaultValue;
  }

  if (pathSplit.length === 0) {
    return (object as Record<string, unknown>)[next] as T;
  }

  return _get(_object, pathSplit, defaultValue);
}
